import React, { useMemo, useState } from 'react'
import { View, StyleSheet } from 'react-native'
import {
  NodeMembersByNodeIdDocument,
  useCreateNodeMemberMutation,
  User,
  useSearchUserQuery,
} from '../../../../generated/graphql'
import type { NodeMembersScreenNode } from './node-member.list'
import { useTranslation } from 'react-i18next'
import Toast from 'react-native-root-toast'
import { useIsFocused } from '@react-navigation/native'
import { PaginatedFlatList } from '../../../../@ui'
import { Button, Avatar, List, Icon } from 'react-native-paper'
import { MelddSearchBar } from '../../../../@ui/form/meldd-search.input'
import { useTheme } from '../../../../hooks/theme.hook'

interface NodeMemberSearchProps {
  node: NodeMembersScreenNode
  onUpdate: () => void
  onSwitchMode: () => void
}

export const NodeMemberSearch: React.FC<NodeMemberSearchProps> = ({
  node,
  onUpdate,
  onSwitchMode,
}) => {
  const { t } = useTranslation('nodes')
  const theme = useTheme()
  const [search, setSearch] = useState<string>('')
  const [addedMembers, setAddedMembers] = useState<string[]>([])
  const [doAddNodeMember, { client }] = useCreateNodeMemberMutation()
  const { data, refetch } = useSearchUserQuery({
    variables: {
      input: {
        username: search,
      },
      nodeId: node.id,
    },
  })

  const isFocused = useIsFocused()

  React.useEffect(() => {
    if (isFocused) {
      refetch()
    }
  }, [isFocused, refetch])

  const onAddMember = async (userId: string) => {
    const input = {
      userId,
      nodeId: node.id,
    }
    await doAddNodeMember({
      variables: { input },
    })

    setAddedMembers((prev) => [...prev, userId])
    Toast.show(t('member-added'))
    client.refetchQueries({ include: [NodeMembersByNodeIdDocument] })
    onUpdate()
  }

  const styles = useMemo(
    () =>
      StyleSheet.create({
        searchContainer: {
          marginBottom: theme.spacing(2),
          flexDirection: 'row',
          gap: theme.spacing(2),
          alignItems: 'center',
        },
      }),
    [theme]
  )

  return (
    <View>
      <View style={styles.searchContainer}>
        <View style={{ flex: 1 }}>
          <MelddSearchBar
            value={search}
            onChangeText={setSearch}
            placeholder={t('search-users-add')}
          />
        </View>
        <Button icon="account-check" onPress={onSwitchMode}>
          {t('manage')}
        </Button>
      </View>
      <PaginatedFlatList
        data={(data?.usersBySearchUsername || []) as any[]}
        onLoadMore={(params: any) => {
          refetch({
            input: {
              ...params,
              username: search,
              offset: data?.usersBySearchUsername.length,
            },
          })
        }}
        renderItem={({ item }: any) => {
          const user = item as User
          const isJustAdded = addedMembers.includes(user.id)
          const isAlreadyAdded = user.isNodeMember || isJustAdded

          return (
            <List.Item
              title={user.username}
              left={() => <Avatar.Text size={32} label={user.username[0]} />}
              right={() =>
                isAlreadyAdded ? (
                  <Icon source="check" size={24} />
                ) : (
                  <Button mode="contained" onPress={() => onAddMember(user.id)}>
                    {t('add')}
                  </Button>
                )
              }
            />
          )
        }}
        total={data?.usersBySearchUsername.length || 0}
        onRefresh={() => {
          refetch({
            input: {
              username: search,
            },
          })
        }}
      />
    </View>
  )
}

export default NodeMemberSearch
